import { calculateSelfWeightFromDensity } from './constants'
import { BeamMaterial, CrossSection, LoadCategory, SMBeamLoad, SMBeamLoadCase, SMBeamModel, SMBeamNode, SMBeamSpan } from './types'
import { calculateSectionProperties } from './utils/sectionProperties'

export type TimberLoadDuration = 'permanent' | 'long-term' | 'medium-term' | 'short-term' | 'instantaneous'

export interface BeamTemplateAction {
  id: string
  name: string
  category: LoadCategory
  /** Characteristic line load on each element in kN/m. Positive is upward. */
  spanIntensities: number[]
}

export interface BeamTemplateSection {
  name: string
  key?: string
  width: number
  height: number
}

export interface BeamTemplateTimberMaterial {
  kind: 'glulam' | 'solid'
  timberClass: string
  elasticModulus: number
  density: number
  loadDuration: TimberLoadDuration
  serviceClass: 1 | 2 | 3
  /** False when decking, purlins, or connected rafters restrain the compression edge. */
  checkLateralTorsionalBuckling: boolean
}

/**
 * Dashboard-to-workspace template contract. The detailed fields are optional so
 * older concrete/steel dashboard payloads continue to use their legacy defaults.
 */
export interface BeamTemplateData {
  templateId: string
  material: BeamMaterial
  timberKind?: 'glulam' | 'solid'
  spanLengths: number[]
  context: string
  designName?: string
  selfWeightName?: string
  section?: BeamTemplateSection
  timberMaterial?: BeamTemplateTimberMaterial
  supportTypes?: SMBeamNode['supportType'][]
  bearingLengthsMm?: Array<number | undefined>
  actions?: BeamTemplateAction[]
}

function uniformLoad(id: string, spanId: number, length: number, category: LoadCategory, intensity: number): SMBeamLoad {
  return {
    id,
    type: 'UniformLoad',
    spanId,
    category,
    pointLoad: 0,
    pointLoadPosition: 0,
    normalForce: 0,
    normalForcePosition: 0,
    normalForceEccentricity: 0,
    uniformIntensity: intensity,
    startIntensity: intensity,
    endIntensity: intensity,
    startPosition: 0,
    endPosition: length
  }
}

function legacyContextAction(context: string, spanCount: number): BeamTemplateAction {
  const contexts: Record<string, { intensity: number; category: 'Variable' | 'Snow'; name: string }> = {
    residential: { intensity: -8.0, category: 'Variable', name: 'Residential live load' },
    commercial: { intensity: -6.0, category: 'Variable', name: 'Office live load' },
    industrial: { intensity: -15.0, category: 'Variable', name: 'Industrial/storage load' },
    parking: { intensity: -5.0, category: 'Variable', name: 'Parking live load' },
    roof: { intensity: -3.0, category: 'Snow', name: 'Snow load' },
    bridge: { intensity: -5.0, category: 'Variable', name: 'Pedestrian load' }
  }
  const resolved = contexts[context] ?? contexts.residential
  return {
    id: 'variable',
    name: resolved.name,
    category: resolved.category,
    spanIntensities: Array.from({ length: spanCount }, () => resolved.intensity)
  }
}

export function createModelFromTemplate(templateData: BeamTemplateData): SMBeamModel {
  const spanCount = templateData.spanLengths.length
  if (spanCount === 0 || templateData.spanLengths.some(length => !Number.isFinite(length) || length <= 0)) {
    throw new Error(`Beam template '${templateData.templateId}' must contain positive span lengths.`)
  }
  if (templateData.supportTypes && templateData.supportTypes.length !== spanCount + 1) {
    throw new Error(`Beam template '${templateData.templateId}' has ${templateData.supportTypes.length} support types for ${spanCount + 1} nodes.`)
  }
  if (templateData.bearingLengthsMm && templateData.bearingLengthsMm.length !== spanCount + 1) {
    throw new Error(`Beam template '${templateData.templateId}' has ${templateData.bearingLengthsMm.length} bearing lengths for ${spanCount + 1} nodes.`)
  }

  const timberDefaults = templateData.timberKind === 'solid'
    ? { E: 11e9, density: 420, width: 48, height: 200 }
    : { E: 12.6e9, density: 430, width: 140, height: 400 }
  const materialDefaults: Record<BeamMaterial, { E: number; density: number; width: number; height: number }> = {
    concrete: { E: 30e9, density: 2500, width: 300, height: 500 },
    steel: { E: 210e9, density: 7850, width: 200, height: 300 },
    timber: timberDefaults
  }
  const legacyDefaults = materialDefaults[templateData.material]
  const defaults = {
    E: templateData.timberMaterial?.elasticModulus ?? legacyDefaults.E,
    density: templateData.timberMaterial?.density ?? legacyDefaults.density,
    width: templateData.section?.width ?? legacyDefaults.width,
    height: templateData.section?.height ?? legacyDefaults.height
  }
  const width = defaults.width / 1000
  const height = defaults.height / 1000
  const area = width * height
  const inertia = width * Math.pow(height, 3) / 12
  const selfWeight = calculateSelfWeightFromDensity(area, defaults.density)
  const totalLength = templateData.spanLengths.reduce((sum, length) => sum + length, 0)

  const defaultSupportTypes: SMBeamNode['supportType'][] = Array.from(
    { length: spanCount + 1 },
    (_, index) => index === 0 ? 'Pinned' : 'Roller'
  )
  const supportTypes = templateData.supportTypes ?? defaultSupportTypes
  const firstRestrainedNode = supportTypes.findIndex(type => type !== 'Free')
  const nodes: SMBeamNode[] = []
  let currentPosition = 0
  for (let index = 0; index <= spanCount; index++) {
    nodes.push({
      id: index + 1,
      supportType: supportTypes[index],
      axialFixed: index === firstRestrainedNode,
      position: currentPosition,
      bearingLengthMm: supportTypes[index] === 'Free' ? undefined : templateData.bearingLengthsMm?.[index]
    })
    if (index < spanCount) {
      currentPosition += templateData.spanLengths[index]
    }
  }

  const actions = templateData.actions ?? [legacyContextAction(templateData.context, spanCount)]
  for (const action of actions) {
    if (action.spanIntensities.length !== spanCount) {
      throw new Error(`Beam template '${templateData.templateId}' action '${action.id}' has ${action.spanIntensities.length} intensities for ${spanCount} spans.`)
    }
    if (action.spanIntensities.some(intensity => !Number.isFinite(intensity))) {
      throw new Error(`Beam template '${templateData.templateId}' action '${action.id}' contains a non-finite line load.`)
    }
  }

  const selfWeightLoads: SMBeamLoad[] = []
  const spans: SMBeamSpan[] = templateData.spanLengths.map((length, spanIndex) => {
    const spanId = spanIndex + 1
    const loads = [uniformLoad(`selfweight-span-${spanId}`, spanId, length, 'Permanent', -selfWeight)]
    selfWeightLoads.push(loads[0])
    for (const action of actions) {
      const intensity = action.spanIntensities[spanIndex]
      if (Math.abs(intensity) > 1e-12) {
        loads.push(uniformLoad(`${action.id}-span-${spanId}`, spanId, length, action.category, intensity))
      }
    }
    return {
      id: spanId,
      leftNodeId: spanId,
      rightNodeId: spanId + 1,
      length,
      E: defaults.E,
      A: area,
      I: inertia,
      loads
    }
  })

  const loadCases: SMBeamLoadCase[] = [
    { id: 'case-selfweight', name: templateData.selfWeightName ?? 'G1 – Self-weight', type: 'Permanent', loads: selfWeightLoads, isActive: true },
    ...actions.map(action => ({
      id: `case-${action.id}`,
      name: action.name,
      type: action.category,
      loads: spans.flatMap(span => span.loads.filter(load => load.id === `${action.id}-span-${span.id}`)),
      isActive: true
    }))
  ]

  let sectionName = templateData.section?.name
  if (!sectionName) {
    if (templateData.material === 'concrete') {
      sectionName = `Rectangular ${defaults.width}×${defaults.height}`
    } else if (templateData.material === 'steel') {
      sectionName = `IPE ${defaults.width}×${defaults.height}`
    } else {
      sectionName = templateData.timberKind === 'solid'
        ? `Solid C24 ${defaults.width}×${defaults.height}`
        : `Glulam GL24h ${defaults.width}×${defaults.height}`
    }
  }
  const baseSection: CrossSection = {
    name: sectionName,
    key: templateData.section?.key,
    width: defaults.width,
    height: defaults.height,
    area,
    inertia,
    // Preserve the legacy dashboard behaviour (catalog-style default) when no detailed section
    // is supplied; a detailed section without a DB key is genuinely custom.
    isCustom: templateData.section ? !templateData.section.key : false,
    material: templateData.material,
    timberKind: templateData.timberMaterial?.kind ?? templateData.timberKind,
    sectionType: 'rectangular',
    sectionClass: 'auto'
  }
  const crossSection: CrossSection = {
    ...baseSection,
    ...calculateSectionProperties(baseSection, templateData.material)
  }

  return {
    nodes,
    spans,
    loadCases,
    crossSection,
    materialSelection: templateData.timberMaterial ? {
      timberKind: templateData.timberMaterial.kind,
      timberClass: templateData.timberMaterial.timberClass,
      timberLoadDuration: templateData.timberMaterial.loadDuration,
      timberServiceClass: templateData.timberMaterial.serviceClass,
      checkLateralTorsionalBuckling: templateData.timberMaterial.checkLateralTorsionalBuckling
    } : undefined,
    rebarZones: templateData.material === 'concrete' ? [{
      start: 0,
      end: totalLength,
      cover: 40,
      layers: [
        { z: -(height * 500 - 48), diameter: 16, count: 3 },
        { z: height * 500 - 48, diameter: 16, count: 2 }
      ]
    }] : undefined,
    designName: templateData.designName ?? '',
    designSessionId: undefined
  }
}
